/**
 * Recommendation attribution — persists the recommendation a diner clicked from so the detail
 * page can attribute `restaurant_viewed` / `reservation_click` to the exact recommendation + rank.
 *
 * Why sessionStorage (not just router nav state): most detail views arrive via a path that loses
 * nav state — a hard reload, a shared/deep link re-entry, or a back/forward restore. Nav state is
 * the fast path; this keyed store is the durable fallback keyed by restaurant `alias` (what the
 * detail route uses in its URL). Scoped to the tab session so it never leaks across sessions.
 */

const STORAGE_KEY = 'rsly_reco_attribution'
// Bound the map so a long browse session can't grow sessionStorage unbounded.
const MAX_ENTRIES = 30

export interface RecommendationAttribution {
  recommendationId: string
  rankPosition?: number
  listType?: string
  // The model version that produced this recommendation, threaded through so the detail page can
  // stamp model_version on the high-intent accept labels (save / reserve) — previously these
  // accepts emitted no model_version, leaving the strongest training labels unattributable to a model.
  modelVersion?: string
}

type StoredAttribution = RecommendationAttribution & { ts: number }
type AttributionStore = Record<string, StoredAttribution>

const readStore = (): AttributionStore => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as AttributionStore) : {}
  } catch {
    return {}
  }
}

/**
 * Record the recommendation/rank a card was clicked from, keyed by restaurant `alias`.
 * No-op when there is no recommendation to attribute (non-reco surfaces) or storage is unavailable.
 */
export const setRecommendationAttribution = (
  alias: string | undefined,
  attribution: RecommendationAttribution,
): void => {
  if (!alias || !attribution.recommendationId) return
  try {
    const store = readStore()
    store[alias] = { ...attribution, ts: Date.now() }
    // Bound the map: keep only the newest MAX_ENTRIES (rebuild rather than delete keys).
    const keys = Object.keys(store)
    const bounded =
      keys.length > MAX_ENTRIES
        ? keys
            .sort((a, b) => store[b].ts - store[a].ts)
            .slice(0, MAX_ENTRIES)
            .reduce<AttributionStore>((acc, k) => {
              acc[k] = store[k]
              return acc
            }, {})
        : store
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(bounded))
  } catch {
    /* sessionStorage unavailable — attribution just won't survive nav-state loss */
  }
}

/** Read the recommendation/rank a restaurant `alias` was last clicked from (if any). */
export const getRecommendationAttribution = (
  alias: string | undefined,
): RecommendationAttribution | undefined => {
  if (!alias) return undefined
  const entry = readStore()[alias]
  if (!entry) return undefined
  const { ts, ...attribution } = entry
  return attribution
}
